import { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Users, BookOpen, GraduationCap, Link2, Shield, ArrowLeft, FileCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useBasePath, usePortal } from '@/contexts/PortalContext';
import TerrainBackground from '@/components/assessment/TerrainBackground';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import CounselorView from '@/pages/admin/CounselorView';
import ConsentManager from '@/components/admin/ConsentManager';

type AdminTab = 'dashboard' | 'students' | 'courses' | 'clubs' | 'codes' | 'consent';

const tabs: { id: AdminTab; label: string; icon: React.ElementType }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'students', label: 'Students', icon: Users },
  { id: 'courses', label: 'Courses', icon: BookOpen },
  { id: 'clubs', label: 'Clubs', icon: GraduationCap },
  { id: 'codes', label: 'Invite Codes', icon: Link2 },
  { id: 'consent', label: 'Consent', icon: FileCheck },
];

interface AdminLayoutProps {
  schoolId: string;
  schoolName: string;
  children: React.ReactNode; // The existing SchoolAdmin content
}

const AdminLayoutWrapper = ({ schoolId, schoolName, children }: AdminLayoutProps) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const basePath = useBasePath();
  const { config } = usePortal();

  return (
    <div className="min-h-screen bg-journey-atmosphere relative overflow-hidden">
      <TerrainBackground count={3} />
      <div className="noise-overlay" />

      {/* Header */}
      <header className="relative z-10 px-6 py-4 flex items-center justify-between border-b border-border/30 bg-background/80 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <Link to={basePath} className="text-primary font-display text-lg font-bold hover:opacity-80 transition-opacity">
            TruePath
          </Link>
          <span className="text-muted-foreground text-xs px-2 py-0.5 rounded-full bg-secondary border border-border">
            {schoolName}
          </span>
          <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
            {config.label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`${basePath}/assessment`}
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Assessment
          </Link>
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Shield className="w-3 h-3 text-accent" /> FERPA Compliant
          </div>
        </div>
      </header>

      <div className="relative z-10 flex">
        {/* Sidebar */}
        <nav className="hidden md:flex flex-col w-56 border-r border-border/30 bg-card/50 backdrop-blur-sm min-h-[calc(100vh-3.5rem)] p-3 gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left ${
                activeTab === tab.id
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              <tab.icon className="w-4 h-4 shrink-0" />
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Mobile tab bar */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-20 bg-card border-t border-border flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
                activeTab === tab.id ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Main content */}
        <main className="flex-1 p-6 md:p-8 max-w-5xl pb-24 md:pb-8">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-display font-bold text-foreground">School Dashboard</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Analytics and insights for {schoolName}
                  </p>
                </div>
                <AdminDashboard schoolId={schoolId} />
              </div>
            )}

            {activeTab === 'students' && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-display font-bold text-foreground">Student Results</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    View and manage student assessment data
                  </p>
                </div>
                <CounselorView schoolId={schoolId} />
              </div>
            )}

            {activeTab === 'consent' && (
              <div className="space-y-6">
                <div>
                  <h1 className="text-2xl font-display font-bold text-foreground">FERPA Consent</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Track parent/guardian consent for student data access
                  </p>
                </div>
                <ConsentManager schoolId={schoolId} />
              </div>
            )}

            {/* Courses, Clubs, Codes — render existing SchoolAdmin content */}
            {(activeTab === 'courses' || activeTab === 'clubs' || activeTab === 'codes') && (
              <div>
                {children}
              </div>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayoutWrapper;